import React from 'react';
import { Table, Box, Button, Textfield } from '@legion-ui/core';
import { FaUserLock, FaUsersSlash, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Indicator from '../Indicator';
import { frontConfig } from '@/config/front-config';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export interface PublisherJourneyType {
  id: number;
  username: string;
  foundUsBy: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  approvedBy: string | null;
  info?: {
    fullname?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  userId?: number;
  fullname?: string;
  email?: string;
  created_at?: Date;
  dateRequest?: Date;
}

interface RequestsTableProps {
  requests: (PublisherJourneyType | undefined)[];
  isLoading: boolean;
  onViewDetail: (request: PublisherJourneyType) => void;
  searchUsername?: string;
  onSearchUsernameChange?: (username: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  sortOrder?: 'ASC' | 'DESC';
  onSortOrderChange?: (order: 'ASC' | 'DESC') => void;
  currentPage?: number;
  itemsPerPage?: number;
}

const RequestsTable = ({
  requests = [],
  isLoading = false,
  onViewDetail,
  searchUsername = '',
  onSearchUsernameChange,
  statusFilter = '',
  onStatusFilterChange,
  sortOrder = 'DESC',
  onSortOrderChange,
  currentPage = 1,
  itemsPerPage = 10,
}: RequestsTableProps) => {
  const navigate = useNavigate();
  const { superAdminUsers } = frontConfig();

  const tableHeader = [
    'No',
    'Full Name',
    'Username',
    'Email',
    'Status',
    'Date Request',
    'Action',
  ];
  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';

    let finalDateString = dateString;
    if (typeof dateString === 'string') {
      finalDateString = dateString.endsWith('Z')
        ? dateString.slice(0, -1)
        : dateString;
    }

    const date =
      typeof finalDateString === 'string'
        ? new Date(finalDateString)
        : finalDateString;

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const maskText = (text: string, visibleChars: number = 6) => {
    if (!text) return text;

    if (text.length <= 3) {
      return '*'.repeat(text.length - 1) + text.slice(-1);
    }

    if (text.length <= visibleChars) {
      const charsToShow = Math.max(1, Math.floor(text.length / 2));
      const maskedLength = text.length - charsToShow;
      const masked = '*'.repeat(maskedLength);
      return masked + text.slice(-charsToShow);
    }

    const maskedLength = text.length - visibleChars;
    const masked = '*'.repeat(Math.min(maskedLength, 10));
    return masked + text.slice(-visibleChars);
  };

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Indicator status="PENDING" />;
      case 'APPROVED':
        return <Indicator status="APPROVED" />;
      case 'REJECTED':
        return <Indicator status="REJECTED" />;
      default:
        return <Indicator status="PENDING" />;
    }
  };

  const handleDetailClick = (request: PublisherJourneyType) => {
    onViewDetail(request);
  };

  const renderRequests = () => {
    const hasValidRequest =
      requests && Array.isArray(requests) && requests.length > 0;

    if (!hasValidRequest) {
      return (
        <tr>
          <td colSpan={7} className="text-center">
            <div className="min-h-[33.25rem] flex flex-col justify-center items-center text-gray-500">
              <FaUsersSlash size={124} />
              <p className="mt-2 text-lg">No user requests found</p>
            </div>
          </td>
        </tr>
      );
    }

    return requests.map(
      (request: PublisherJourneyType | undefined, index: number) => {
        if (!request) {
          return null;
        }

        const fullname =
          request.info?.fullname ||
          request.fullname ||
          request.username ||
          'Unknown User';
        const email =
          request.info?.email ||
          request.email ||
          `${request.username}@unknown.com`;

        return (
          <tr key={request.id}>
            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
            <td>{maskText(fullname, 4)}</td>
            <td>{maskText(request.username, 4)}</td>
            <td>{maskText(email, 5)}</td>
            <td>{getStatusIndicator(request.status)}</td>

            <td>
              {request.createdAt ? formatDate(request.createdAt) + ' WIB' : '-'}
            </td>
            <td>
              <Box width="160px">
                {superAdminUsers.includes(request.username) ? (
                  <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-white bg-blue-600 rounded-full">
                    <FaUserLock className="mr-1" />
                    Super Admin
                  </span>
                ) : (
                  <Button
                    iconLeft={<FaUserLock />}
                    variant="outline"
                    type="button"
                    onClick={() => handleDetailClick(request)}
                  >
                    Detail
                  </Button>
                )}
              </Box>
            </td>
          </tr>
        );
      },
    );
  };

  return (
    <div>
      <div className="mb-4 space-y-4 md:space-y-0 md:flex md:gap-4 md:items-end">
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search by Username
          </label>
          <div className="relative">
            <Textfield
              placeholder="Search exact username..."
              before={<FaSearch className="text-gray-400" />}
              value={searchUsername}
              onChange={(e) =>
                onSearchUsernameChange?.((e.target as HTMLInputElement).value)
              }
              className="pl-10"
            />
          </div>
        </div>

        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange?.(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>

        <div className="min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort by Date
          </label>
          <select
            value={sortOrder}
            onChange={(e) =>
              onSortOrderChange?.(e.target.value as 'ASC' | 'DESC')
            }
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="DESC">Newest First</option>
            <option value="ASC">Oldest First</option>
          </select>
        </div>
      </div>

      <Table hoverable responsive className="!overflow-x-auto">
        <thead className="whitespace-nowrap">
          <tr>
            {tableHeader.map((item, index) => (
              <th key={index}>{item}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <>
              {[...Array(5)].map((_, rowIdx) => (
                <tr key={rowIdx}>
                  {Array(tableHeader.length)
                    .fill(0)
                    .map((_, colIdx) => (
                      <td key={colIdx}>
                        <Skeleton height={24} />
                      </td>
                    ))}
                </tr>
              ))}
            </>
          ) : (
            renderRequests()
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default RequestsTable;
